import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { PushNotifications, type PushNotificationSchema, type ActionPerformed, type Token } from '@capacitor/push-notifications';
import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';
import { PlatformService } from '../platform/platform.service';
import { TenantService } from '../../services/tenant.service';
import { AuthService } from '../../services/auth.service';
import { InAppNotificationService } from './in-app-notification.service';
import { NotificationStore } from './notification.store';
import { resolveNotificationRoute } from '../navigation/route-normalization.util';

interface DeviceRegisterRequest {
  token: string;
  platform: 'ANDROID';
  device_id?: string;
}

interface PushRuntimePlugin {
  isFirebaseReady(): Promise<{ ready: boolean; error?: string }>;
}

type PushRuntimeGlobal = typeof globalThis & {
  __sportowcyPushRuntimePlugin?: PushRuntimePlugin;
};

const pushRuntimeGlobal = globalThis as PushRuntimeGlobal;
const PushRuntime =
  pushRuntimeGlobal.__sportowcyPushRuntimePlugin ?? registerPlugin<PushRuntimePlugin>('PushRuntime');

if (!pushRuntimeGlobal.__sportowcyPushRuntimePlugin) {
  pushRuntimeGlobal.__sportowcyPushRuntimePlugin = PushRuntime;
}


@Injectable({
  providedIn: 'root'
})
export class MobilePushService {
  private initialized = false;
  private pendingToken: string | null = null;
  private readonly listeners: PluginListenerHandle[] = [];

  constructor(
    private readonly platformService: PlatformService,
    private readonly tenantService: TenantService,
    private readonly authService: AuthService,
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly inAppNotificationService: InAppNotificationService,
    private readonly notificationStore: NotificationStore,
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized || !this.isCapacitorRuntime()) {
      return;
    }

    const isFirebaseReady = await this.isFirebaseReady();
    if (!isFirebaseReady) {
      return;
    }

    this.initialized = true;

    this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated) {
        this.registerPendingToken().catch(() => undefined);
      }
    });

    const registrationListener = await PushNotifications.addListener('registration', (token: Token) => {
      this.pendingToken = token.value;
      this.registerPendingToken().catch(() => undefined);
    });
    this.listeners.push(registrationListener);

    const registrationErrorListener = await PushNotifications.addListener('registrationError', (error: unknown) => {
      console.error('Push registration error', error);
    });
    this.listeners.push(registrationErrorListener);

    const notificationReceivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
      this.inAppNotificationService.showFromPush(notification);
      this.notificationStore.onForegroundPushReceived().catch(() => undefined);
    });
    this.listeners.push(notificationReceivedListener);

    const actionPerformedListener = await PushNotifications.addListener('pushNotificationActionPerformed', (event: ActionPerformed) => {
      this.handlePushAction(event).catch(() => undefined);
    });
    this.listeners.push(actionPerformedListener);

    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') {
      return;
    }

    await PushNotifications.register();
  }

  private isCapacitorRuntime(): boolean {
    return this.platformService.isBrowser() && this.platformService.getRuntimePlatform() === 'capacitor';
  }

  private async isFirebaseReady(): Promise<boolean> {
    try {
      const response = await PushRuntime.isFirebaseReady();
      return response.ready === true;
    } catch (error) {
      console.warn('PushRuntime plugin unavailable, skipping push registration.', error);
      return false;
    }
  }

  private async registerPendingToken(): Promise<void> {
    if (!this.pendingToken || !this.authService.isAuthenticated()) {
      return;
    }

    const tenant = this.tenantService.getCurrentTenantSync();
    if (!tenant?.schema_name) {
      return;
    }

    const requestBody: DeviceRegisterRequest = {
      token: this.pendingToken,
      platform: 'ANDROID',
    };

    const apiBaseUrl = this.tenantService.getApiBaseUrl();
    await firstValueFrom(this.http.post(`${apiBaseUrl}/api/devices/register/`, requestBody));
  }

  private async handlePushAction(event: ActionPerformed): Promise<void> {
    const data = event.notification?.data ?? {};
    const tenant = this.readDataValue(data, 'tenant');
    if (tenant) {
      this.tenantService.setCurrentTenantSchema(tenant);
    }

    const normalizedRoute = resolveNotificationRoute(data);
    if (normalizedRoute) {
      await this.router.navigateByUrl(normalizedRoute);
      const notificationIdFromRoute = this.extractNotificationIdFromRoute(normalizedRoute);
      if (notificationIdFromRoute) {
        await this.notificationStore.markAsRead(notificationIdFromRoute).catch(() => undefined);
      }
      return;
    }

    const notificationId = this.readDataValue(data, 'notification_id');
    if (notificationId) {
      await this.router.navigateByUrl(`/dashboard?notification=${encodeURIComponent(notificationId)}`);
      return;
    }

    await this.router.navigateByUrl('/dashboard');
  }

  private extractNotificationIdFromRoute(route: string): number | null {
    const match = route.match(/^\/notifications\/(\d+)(?:$|[?#/])/);
    if (!match?.[1]) {
      return null;
    }

    const notificationId = Number(match[1]);
    if (Number.isNaN(notificationId) || notificationId <= 0) {
      return null;
    }

    return notificationId;
  }

  private readDataValue(data: Record<string, unknown>, key: string): string {
    const value = data[key];
    if (typeof value === 'string') {
      return value;
    }
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }
}